import { useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { FilterBar } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyResponseSummaryDto } from '@/shared/api/types'
import { listAssignments, listPolls, listResponses } from './api'
import { PollSelect } from './PollSelect'
import { ResponseDrawer } from './ResponseDrawer'
import { CODE_LIST_UNAVAILABLE, useCodes } from './useVocabulary'
import {
  Pill, count, dispositionLabel, duration, num, responseStatusLabel,
} from './vocab'

/**
 * Every interview on a poll.
 *
 * ## Why this screen did not exist before
 *
 * `GET /surveys/{id}/responses` had been served since the capture feature
 * shipped and **no screen called it**. The only route to an interview was the
 * Quality queue, which shows the ones an integrity rule flagged — so an
 * unflagged interview, which is the overwhelming majority of them, could not be
 * read at all.
 *
 * ## Three authorities, three different things
 *
 * - **`survey:read-responses`** opens this screen. It was split out from
 *   `survey:read` because reading a poll's design and paging through four
 *   hundred people's individual answers are different disclosures, and one
 *   authority covering both left a deployment unable to express either.
 * - **`survey:read-raw`** turns the masks into names. Nothing on this page shows
 *   an identifier without it, and each reveal is one interview and one audit row.
 * - **`survey:listen`** plays the audio, inside the drawer, and every play is
 *   audited.
 *
 * A caller with only the first sees initials, a three-digit phone tail and a
 * silent player — which is the correct amount for reading answers.
 *
 * ## The drawer is the Quality drawer
 *
 * Not a second interview view. `ResponseDrawer` already renders the answers, the
 * flags, the audio player and the masks; a copy of it here would be the copy
 * that drifts.
 */
export function InterviewsPage() {
  const canRead = useCan('survey:read-responses')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const status = params.get('status') ?? ''
  const disposition = params.get('disposition') ?? ''
  const mobilizerId = params.get('interviewer') ?? ''
  const page = Number(params.get('page') ?? '0')
  const openId = params.get('interview')

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    // Any filter change returns to the first page. Staying on page 4 of a list
    // that now has one page shows an empty table under a filter that matched.
    if (key !== 'page' && key !== 'interview') next.delete('page')
    setParams(next, { replace: true })
  }

  const statuses = useCodes('responseStatus')
  const dispositions = useCodes('contactOutcome')

  const polls = useLoad(() => listPolls({}, 0, 200), [])
  const interviewers = useLoad(
    () => (pollId ? listAssignments(pollId) : Promise.resolve([])), [pollId])
  const rows = useLoad(
    () => (pollId
      ? listResponses(pollId, {
          statusCode: status || undefined,
          dispositionCode: disposition || undefined,
          mobilizerId: mobilizerId || undefined,
        }, page, 50)
      : Promise.resolve(null)),
    [pollId, status, disposition, mobilizerId, page])

  if (!canRead) return <Forbidden what="the interviews on a poll" />

  const data = rows.data
  const content = data?.content ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Interviews"
        description="Every interview collected on a poll — its answers, its audio and who conducted it."
        right={(
          <PollSelect polls={polls.data?.content ?? []} value={pollId}
            onChange={(id) => set('poll', id)} />
        )}
      />

      <ErrorStrip message={rows.error} />

      {!pollId ? (
        <Panel><Note>Pick a poll to read the interviews collected on it.</Note></Panel>
      ) : (
        <>
          <FilterBar>
            <label className="flex items-center gap-1.5 text-erp-xs">
              Status
              <select className="erp-input h-9" value={status}
                disabled={statuses.loading || statuses.unavailable}
                onChange={(e) => set('status', e.target.value)}>
                <option value="">All</option>
                {statuses.codes.map((c) => (
                  <option key={c} value={c}>{responseStatusLabel(c)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-erp-xs">
              Outcome
              <select className="erp-input h-9" value={disposition}
                disabled={dispositions.loading || dispositions.unavailable}
                onChange={(e) => set('disposition', e.target.value)}>
                <option value="">All</option>
                {dispositions.codes.map((c) => (
                  <option key={c} value={c}>{dispositionLabel(c)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-erp-xs">
              Interviewer
              <select className="erp-input h-9" value={mobilizerId}
                onChange={(e) => set('interviewer', e.target.value)}>
                <option value="">Everybody</option>
                {(interviewers.data ?? [])
                  .filter((a) => a.mobilizerId)
                  .map((a) => (
                    <option key={a.mobilizerId} value={a.mobilizerId}>
                      {a.mobilizerName ?? a.mobilizerId}
                    </option>
                  ))}
              </select>
            </label>
            {(statuses.unavailable || dispositions.unavailable) && (
              <span className="text-erp-xs text-red-700">{CODE_LIST_UNAVAILABLE}</span>
            )}
          </FilterBar>

          <Panel
            title={data ? `${count(data.totalElements)} interview${data.totalElements === 1 ? '' : 's'}` : 'Interviews'}
            description="Identifiers are masked. Opening one shows its answers and, with the right authority, its audio."
          >
            {rows.loading && content.length === 0 ? (
              <LoadingBlock label="Reading the interviews…" />
            ) : (
              <>
                <ErpTable head={(
                  <>
                    <Th>Respondent</Th>
                    <Th>Interviewer</Th>
                    <Th>Where</Th>
                    <Th>Status</Th>
                    <Th>Outcome</Th>
                    <Th className="text-right">Answered</Th>
                    <Th className="text-right">Duration</Th>
                    <Th className="text-right">Quality</Th>
                    <Th>Submitted</Th>
                  </>
                )}>
                  {content.length === 0 ? (
                    <EmptyRow colSpan={9}
                      message={status || disposition || mobilizerId
                        ? 'No interview on this poll matches those filters.'
                        : 'No interviews have been collected on this poll yet.'} />
                  ) : content.map((r) => (
                    <tr key={r.id}
                      className="hover:bg-muted/40 cursor-pointer"
                      onClick={() => set('interview', r.id)}>
                      <Td>
                        <span className="block">{r.respondentNameMask ?? '—'}</span>
                        <span className="block text-erp-xs text-muted-foreground">
                          {r.respondentPhoneMask ?? 'no phone'}
                        </span>
                      </Td>
                      <Td>{r.mobilizerName ?? '—'}</Td>
                      <Td>{r.unitName ?? '—'}</Td>
                      <Td><Pill>{responseStatusLabel(r.statusCode)}</Pill></Td>
                      <Td>{dispositionLabel(r.dispositionCode)}</Td>
                      <Td className="text-right tabular-nums">
                        {count(r.answeredCount)}
                        {r.blankCount > 0 && (
                          <span className="text-erp-xs text-muted-foreground">
                            {' '}/ {count(r.blankCount)} blank
                          </span>
                        )}
                      </Td>
                      <Td className="text-right tabular-nums">{duration(r.durationSeconds)}</Td>
                      <Td className="text-right tabular-nums">
                        {/* Null, not zero: a score that has not been computed and a
                            score of nought are different statements. */}
                        {r.qualityScore === null ? '—' : num(r.qualityScore, 0)}
                      </Td>
                      <Td>{r.submittedAt ? fmtDateTime(r.submittedAt) : '—'}</Td>
                    </tr>
                  ))}
                </ErpTable>

                {data && data.totalPages > 1 && (
                  <div className="flex items-center justify-between gap-3 pt-3">
                    <span className="text-erp-xs text-muted-foreground">
                      Page {data.page + 1} of {data.totalPages}
                    </span>
                    <span className="flex gap-2">
                      <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                        disabled={data.page === 0}
                        onClick={() => set('page', String(data.page - 1))}>
                        Previous
                      </button>
                      <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                        disabled={data.page + 1 >= data.totalPages}
                        onClick={() => set('page', String(data.page + 1))}>
                        Next
                      </button>
                    </span>
                  </div>
                )}
              </>
            )}
          </Panel>
        </>
      )}

      <ResponseDrawer key={openId ?? 'none'}
        responseId={openId}
        onClose={() => set('interview', '')}
        onChanged={() => rows.reload()} />
    </div>
  )
}

/** Kept for the table's type import to stay honest about what a row is. */
export type InterviewRow = SurveyResponseSummaryDto
